import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronUp, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { useParams } from 'react-router'
import { cn } from '@/lib/utils'
import { useContactTimeline } from '@/api/timeline'
import { usePlanRights } from '@/hooks/usePlanRights'
import type { TimelineEvent } from '@/types/timeline'
import type { EchangeCible } from '@/stores/useUIStore'
import { motion } from 'framer-motion'
import { TimelineEventCard } from './TimelineEventCard'

interface Props {
  phoneNumber: string
  /** Seulement les échanges de cette ligne ; null : toutes les lignes. */
  line?: string | null
  darkMode?: boolean
  /** Opened on a search result: scroll to this exchange and highlight it. */
  cible?: EchangeCible | null
}

type Translator = (key: string, defaultValue?: string) => string

function buildDateLabel(date: Date, t: Translator, locale: 'fr' | 'en'): string {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (date >= today) return t('contacts.timeline.today', "Aujourd'hui")
  if (date >= yesterday) return t('contacts.timeline.yesterday', 'Hier')
  return format(date, 'd MMMM yyyy', { locale: locale === 'en' ? enUS : fr })
}

export function ConversationTimeline({ phoneNumber, line = null, darkMode = false, cible = null }: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const query = useContactTimeline(phoneNumber, line, cible?.at ?? null)
  // Commenter un appel ou un texto : plan Team (lire reste ouvert à tous).
  const { allowed } = usePlanRights()
  const equipe = allowed('collaboration_equipe')
  const containerRef = useRef<HTMLDivElement>(null)
  const initialScrolledRef = useRef(false)

  // Aplatir et inverser : backend renvoie anti-chrono, on affiche chrono
  const events: TimelineEvent[] = useMemo(() => {
    const all = query.data?.pages.flatMap((p) => p.events) || []
    return [...all].reverse()
  }, [query.data])

  // First load: scroll to the bottom (latest exchanges at the bottom), or to
  // the exchange a search result points to, highlighted for a moment.
  const [surligne, setSurligne] = useState<string | null>(null)
  const cibleVue = useRef<string | null>(null)
  const cibleId = cible?.eventId ?? null
  useEffect(() => {
    const el = containerRef.current
    if (query.isLoading || events.length === 0 || !el) return
    if (cibleId && cibleVue.current !== cibleId) {
      const carte = el.querySelector<HTMLElement>(`[data-event-id="${CSS.escape(cibleId)}"]`)
      if (carte) {
        cibleVue.current = cibleId
        initialScrolledRef.current = true
        carte.scrollIntoView({ block: 'center' })
        setSurligne(cibleId)
        return
      }
    }
    if (!initialScrolledRef.current) {
      el.scrollTop = el.scrollHeight
      initialScrolledRef.current = true
    }
  }, [query.isLoading, events.length, cibleId])

  useEffect(() => {
    if (!surligne) return
    const fin = setTimeout(() => setSurligne(null), 2500)
    return () => clearTimeout(fin)
  }, [surligne])

  // Un échange qui arrive pendant qu'on lit (texto, appel) entre en montant ;
  // ceux du premier chargement et les plus anciens (« Voir plus ») non.
  // `dernierVu` : l'échange le plus récent déjà affiché.
  const dernierVu = useRef<string | null>(null)
  const plusRecent = events.length ? events[events.length - 1].timestamp : null
  useEffect(() => {
    if (!plusRecent) return
    const avant = dernierVu.current
    dernierVu.current = plusRecent
    if (avant === null || plusRecent <= avant) return
    // On suit le nouvel échange si l'on était déjà en bas du fil.
    const el = containerRef.current
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 400) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    }
  }, [plusRecent])

  // Quand on charge plus, maintenir la position visuelle
  const scrollHeightBeforeRef = useRef(0)
  const handleLoadMore = () => {
    if (!query.hasNextPage || query.isFetchingNextPage) return
    if (containerRef.current) {
      scrollHeightBeforeRef.current = containerRef.current.scrollHeight
    }
    void query.fetchNextPage().then(() => {
      requestAnimationFrame(() => {
        if (!containerRef.current) return
        const after = containerRef.current.scrollHeight
        const diff = after - scrollHeightBeforeRef.current
        containerRef.current.scrollTop += diff
      })
    })
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div
        ref={containerRef}
        className={cn(
          'flex-1 overflow-y-auto min-h-0 overscroll-contain',
          darkMode ? 'bg-slate-950' : 'bg-gray-50',
        )}
      >
        {query.hasNextPage && !query.isLoading && (
          <div className="px-2 pt-2 pb-1">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={query.isFetchingNextPage}
              className={cn(
                'w-full h-7 text-xs flex items-center justify-center gap-1 rounded transition-colors',
                darkMode
                  ? 'text-slate-400 hover:text-indigo-300 hover:bg-slate-900/50'
                  : 'text-gray-600 hover:text-indigo-600 hover:bg-white/60',
              )}
            >
              {query.isFetchingNextPage ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <ChevronUp className="h-3 w-3" />
              )}
              {t('contacts.timeline.loadMore', 'Voir plus')}
            </button>
          </div>
        )}

        {query.isLoading && events.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <Loader2
              className={cn(
                'h-5 w-5 animate-spin',
                darkMode ? 'text-slate-500' : 'text-gray-400',
              )}
            />
          </div>
        ) : events.length === 0 ? (
          <div
            className={cn(
              'flex items-center justify-center h-full text-sm',
              darkMode ? 'text-slate-500' : 'text-gray-500',
            )}
          >
            {t('contacts.timeline.empty', 'Aucune interaction')}
          </div>
        ) : (
          <div className="p-4 space-y-1">
            {events.map((event, idx) => {
              const eventDate = new Date(event.timestamp)
              const prevDate = idx > 0 ? new Date(events[idx - 1].timestamp) : null
              const showDateSep =
                !prevDate || eventDate.toDateString() !== prevDate.toDateString()
              return (
                <Fragment key={event.id}>
                  {showDateSep && (
                    <div className="flex justify-center py-2">
                      <span
                        className={cn(
                          'text-[11px] font-semibold uppercase tracking-widest',
                          darkMode ? 'text-slate-600' : 'text-slate-400',
                        )}
                      >
                        {buildDateLabel(
                          eventDate,
                          t as unknown as Translator,
                          locale as 'fr' | 'en',
                        )}
                      </span>
                    </div>
                  )}
                  <motion.div
                    data-event-id={event.id}
                    initial={dernierVu.current !== null && event.timestamp > dernierVu.current ? { opacity: 0, y: 10 } : false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}
                    className={cn(
                      'rounded-lg transition-shadow duration-700',
                      surligne === event.id &&
                        (darkMode
                          ? 'ring-2 ring-amber-400/60 ring-offset-2 ring-offset-slate-950'
                          : 'ring-2 ring-amber-400/70 ring-offset-2 ring-offset-gray-50'),
                    )}
                  >
                    <TimelineEventCard
                      event={event}
                      darkMode={darkMode}
                      phoneNumber={phoneNumber}
                      line={line}
                      equipe={equipe}
                    />
                  </motion.div>
                </Fragment>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
